import { DocuGistQuantityAggregation } from '@dealpoint/deprecated-lib/src/Models'
import getSearchQueryDescription from '@dealpoint/lib/src/getSearchQueryDescription'
import { IContractSearchQuery, ITerm } from '@dealpoint/types'
import { PayloadAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'
import { omit, sortBy } from 'lodash'

import { Utils } from '../Utils'
import { IAggregateSearch, PartialAtLeast, SearchId, Status } from '../types'
import removeIndex from '../utils/array/removeIndex'
import searchDocuments from '../utils/document/pagedSearch'
import combineFilters from '../utils/searchQuery/combineFilters'
import createContractSearchQuery from '../utils/searchQuery/createContractSearchQuery'

// eslint-disable-next-line import/no-cycle
import { RootState } from './types'

// Helpers
const FIRST_SEARCH_ID = Utils.generateId()

interface CreateSearchProps {
  description: string
  id?: SearchId
  matchedTermTypes?: string[]
  status?: Status
}

const _createSearch = ({
  description = 'Untitled',
  id,
  matchedTermTypes,
  status = 'init',
}: CreateSearchProps): IAggregateSearch => ({
  aggregate: 'avg',
  id: id ?? Utils.generateId(),
  description,
  originalTotalRecords: 0,
  searchQuery: createContractSearchQuery({
    active: true,
    endTimeUnset: true,
    expired: true,
    matchedTermTypes,
  }),
  status,
  submittedSearchQuery: null,
  view: 'vertical',
})

const filterRecords = (records: DocuGistQuantityAggregation[], termTypes: ITerm[]) => {
  return sortBy(termTypes, 'title').map((termType) => ({
    termType,
    ...records.find((r) => r.termType?.id === termType.id),
  }))
}

const getOriginalTotalRecords = (
  search: IAggregateSearch,
  nextTotalRecords: number,
  includeIds: string[]
) => {
  if (includeIds?.length) {
    return search.originalTotalRecords ? search.originalTotalRecords : search.results.totalRecords
  }
  return nextTotalRecords
}

// Slice
interface ISearchesState {
  filteredIds: Record<SearchId, string[]>
  selectedId: SearchId
  searches: IAggregateSearch[]
}

const initialState: ISearchesState = {
  filteredIds: {},
  selectedId: FIRST_SEARCH_ID,
  searches: [_createSearch({ description: 'All contracts', id: FIRST_SEARCH_ID })],
}

const slice = createSlice({
  name: 'aggregateSearches',
  initialState,
  reducers: {
    createSearch(
      state,
      action: PayloadAction<{ description: string; matchedTermTypes?: string[] }>
    ) {
      const { description, matchedTermTypes } = action.payload
      const newSearch = _createSearch({ description, matchedTermTypes, status: 'idle' })
      state.searches.push(newSearch)
      state.selectedId = newSearch.id
    },
    deleteSearch(state, action: PayloadAction<SearchId>) {
      const { searches } = state
      const index = searches.findIndex(({ id }) => id === action.payload)
      const newSearches = removeIndex(searches, index)

      state.searches = newSearches

      const newIndex = newSearches.length > 1 ? Math.max(0, index - 1) : 0
      state.selectedId = newSearches[newIndex].id

      // Delete any selected documents associated with this search
      state.filteredIds = omit(state.filteredIds, action.payload)
    },
    deleteSelectedDocuments(state, action: PayloadAction<SearchId>) {
      state.filteredIds = omit(state.filteredIds, action.payload)
    },
    setSelectSearch(state, action: PayloadAction<SearchId>) {
      state.selectedId = action.payload
    },
    setSelectedDocuments(state, action: PayloadAction<{ id: SearchId; documentIds: string[] }>) {
      const { id, documentIds } = action.payload
      state.filteredIds[id] = documentIds
    },
    updateSearch(state, action: PayloadAction<PartialAtLeast<IAggregateSearch, 'id'>>) {
      _updateSearch(state, action.payload)
    },
    updateSearches(state, action: PayloadAction<PartialAtLeast<IAggregateSearch, 'id'>[]>) {
      action.payload.forEach((search) => _updateSearch(state, search))
    },
  },
})

// Helpers
const getDescription = (terms: ITerm[], searchQuery: IContractSearchQuery) => {
  const description = getSearchQueryDescription(terms, searchQuery)

  return !description || description.length === 0 ? 'All contracts' : description
}

const _updateSearch = (state: ISearchesState, partial: PartialAtLeast<IAggregateSearch, 'id'>) => {
  const index = state.searches.findIndex(({ id }) => id === partial.id)
  const search = state.searches[index]
  state.searches[index] = {
    ...search,
    ...partial,
    searchQuery: {
      ...search.searchQuery,
      ...partial.searchQuery,
    },
  }
}

// Actions
interface IGetSearchResultsProps {
  allTerms: ITerm[]
  id: SearchId
  includeIds?: string[]
  page?: number
  pageSize?: number
  termTypes: ITerm[]
  url: string
}

export const getSearchResults = createAsyncThunk(
  'aggregateSearches/getResults',
  async (
    { allTerms, id, includeIds, page, pageSize, termTypes, url }: IGetSearchResultsProps,
    { dispatch, getState }
  ) => {
    const searches = selectAggregateSearches(getState() as RootState)
    const search = searches.find((s) => s.id === id)
    if (!search) {
      return
    }

    dispatch(updateSearch({ id, error: false, status: 'loading' }))
    try {
      const { searchQuery } = search
      const { results } = await searchDocuments<DocuGistQuantityAggregation>({
        includeIds,
        page,
        pageSize,
        searchQuery: {
          ...omit(searchQuery, ['contractInformationFilters', 'keyTermFilters']),
          filters: combineFilters(searchQuery),
        },
        url,
      })

      // Get the search description
      const description = getDescription(allTerms, searchQuery)

      const filteredRecords = filterRecords(results.records, termTypes)
      const selectedDocuments = selectAggregateSelectedDocuments(getState() as RootState)

      dispatch(
        updateSearch({
          id,
          description,
          filteredRecords,
          originalTotalRecords: getOriginalTotalRecords(
            {
              ...search,
              originalTotalRecords: selectedDocuments[id] ? search.originalTotalRecords : undefined,
            },
            results.totalRecords,
            includeIds
          ),
          results,
          status: 'idle',
          submittedSearchQuery: search.searchQuery,
        })
      )
    } catch (error) {
      dispatch(updateSearch({ id, error: true, status: 'error' }))
    }
  }
)

export const filterSearchResults = createAsyncThunk(
  'aggregateSearches/filterResults',
  (termTypes: ITerm[], { dispatch, getState }) => {
    const searches = selectAggregateSearches(getState() as RootState)
    dispatch(
      updateSearches(
        searches.map((s) => ({
          id: s.id,
          filteredRecords: filterRecords(s.results.records, termTypes),
        }))
      )
    )
  }
)

export const {
  createSearch,
  deleteSearch,
  deleteSelectedDocuments,
  setSelectSearch,
  setSelectedDocuments,
  updateSearch,
  updateSearches,
} = slice.actions

// Selectors
const selectRoot = (state: RootState) => state.aggregateSearches

export const selectAggregateSearches = createSelector(selectRoot, ({ searches }) => searches)

export const selectSelectedId = createSelector(selectRoot, ({ selectedId }) => selectedId)

export const selectSelectedAggregateSearch = createSelector(
  selectAggregateSearches,
  selectSelectedId,
  (searches, selectedId) => searches.find((s) => s.id === selectedId)
)

export const selectAggregateSelectedDocuments = createSelector(
  selectRoot,
  ({ filteredIds }) => filteredIds
)

// Reduce
export default slice.reducer
